import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import AlertsService from "services/AdminConsole/Monitoring/Profiles/MonitoringProfilesService";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiCheckbox,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSelectable,
  EuiSelectableOption,
  EuiSpacer,
} from "@elastic/eui";
import { NotificationChannel } from "../../profileTypes";
import { useNewProfileNotificationsStyles, useProfileStyles } from "../../profileStyles";
import BadgeCount from "components/Badge/BadgeCount";
import LinkToList from "components/Popover/LinkToList";
import AlertIcon from "components/Icons/AlertIcon";
import { SEVERITY_TYPES, SeverityType } from "../../profileConsts";
import Popover from "components/Popover/Popover";
import { SEVERITY_MAP, SeverityString } from "../../profileUtils";
import Button from "components/Buttons/Button";

interface Props {
  selectedNotificationChannels: {
    alertOpenAssignments: { severityTypeId: number; channels: { channelId: number }[] }[];
    alertCloseAssignments: { severityTypeId: number; channels: { channelId: number }[] }[];
  };
  setSelectedNotificationChannels: any;
  getUpdatedAlertTypes: any;
  setIsDirty: (isDirty: boolean) => void;
}

const alertTypeColumns = [
  {
    field: "name",
    name: "Name",
  },
  {
    field: "toleranceTypeName",
    name: "Tolerance",
    width: 150,
    render: (tolerance) => {
      return (
        <div>
          <strong>Tolerance: </strong>
          <span>{tolerance}</span>
        </div>
      );
    },
  },
];

const ProfileNotifications = ({
  selectedNotificationChannels,
  setSelectedNotificationChannels,
  getUpdatedAlertTypes,
  setIsDirty,
}: Props) => {
  const classes = useNewProfileNotificationsStyles();
  const sharedClasses = useProfileStyles();
  const { clientId } = useSelector(globalFilterSelector, shallowEqual);
  const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPopoverOpen, setIsPopoverOpen] = useState<{ [key: string]: string | null }>({});
  const [severityOptions, setSeverityOptions] = useState<
    Record<string, Record<string, EuiSelectableOption[]>>
  >({});

  const getNotificationChannels = async () => {
    try {
      const result = await AlertsService.getNotificationChannels({
        clientId,
      });
      if (result?.channels) {
        setNotificationChannels(result.channels);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getNotificationChannels();
  }, [clientId]);

  useEffect(() => {
    if (notificationChannels.length > 0) {
      const rows = Array.from(SEVERITY_TYPES).map(([id, type]) => type);
      const newSeverityOptions: Record<string, Record<string, EuiSelectableOption[]>> = {};

      rows.forEach((severity) => {
        const severityTypeId = SEVERITY_MAP.strings.get(severity as SeverityString);
        if (!severityTypeId) return;

        newSeverityOptions[severity] = {
          open: notificationChannels.map((channel) => ({
            label: `${channel.type}: ${channel.name}`,
            key: channel.id.toString(),
            checked: selectedNotificationChannels.alertOpenAssignments?.some(
              (assignment) =>
                assignment.severityTypeId === severityTypeId &&
                assignment.channels.some((c) => c.channelId === channel.id)
            )
              ? ("on" as const)
              : undefined,
          })),
          closed: notificationChannels.map((channel) => ({
            label: `${channel.type}: ${channel.name}`,
            key: channel.id.toString(),
            checked: selectedNotificationChannels.alertCloseAssignments?.some(
              (assignment) =>
                assignment.severityTypeId === severityTypeId &&
                assignment.channels.some((c) => c.channelId === channel.id)
            )
              ? ("on" as const)
              : undefined,
          })),
        };
      });

      setSeverityOptions(newSeverityOptions);
    }
  }, [notificationChannels, selectedNotificationChannels]);

  const rows = Array.from(SEVERITY_TYPES).map(([id, type]) => type);

  const handleOptionsChange = (
    newOptions: EuiSelectableOption[],
    severity: string,
    notificationType: string
  ) => {
    const checked = newOptions.filter((o) => o.checked === "on");
    const selectedChannels = checked
      .map((o) => {
        const channel = notificationChannels.find(
          (c) => o.key !== undefined && c.id.toString() === o.key
        );

        if (!channel) return null;

        return {
          channelId: channel.id,
        };
      })
      .filter((channel): channel is { channelId: number } => channel !== null);

    setSelectedNotificationChannels((prev) => {
      const severityTypeId = SEVERITY_MAP.strings.get(severity as SeverityString);
      if (!severityTypeId) return prev;

      const assignmentType =
        notificationType === "open" ? "alertOpenAssignments" : "alertCloseAssignments";

      const updatedAssignments = [...(prev[assignmentType] || [])];

      const severityIndex = updatedAssignments.findIndex(
        (a) => a.severityTypeId === severityTypeId
      );

      if (severityIndex !== -1) {
        updatedAssignments[severityIndex] = {
          ...updatedAssignments[severityIndex],
          channels: selectedChannels,
        };
      } else {
        updatedAssignments.push({
          severityTypeId,
          channels: selectedChannels,
        });
      }

      return {
        ...prev,
        [assignmentType]: updatedAssignments,
      };
    });

    setSeverityOptions((prev) => ({
      ...prev,
      [severity]: {
        ...prev[severity],
        [notificationType]: newOptions,
      },
    }));

    setIsDirty(true);
  };

  const handlePopoverToggle = (severity: string, notificationType: string) => {
    setIsPopoverOpen((prev) => ({
      ...prev,
      [`${severity}-${notificationType}`]: prev[`${severity}-${notificationType}`]
        ? null
        : `${severity}-${notificationType}`,
    }));
  };

  const getSelectText = (severity: string, notificationType: string) => {
    const severityTypeId = SEVERITY_MAP.strings.get(severity as SeverityString);
    if (!severityTypeId) {
      return <span className={sharedClasses.selectText}>Select</span>;
    }

    const assignments =
      notificationType === "open"
        ? selectedNotificationChannels.alertOpenAssignments
        : selectedNotificationChannels.alertCloseAssignments;

    const severityAssignment = assignments?.find(
      (assignment) => assignment.severityTypeId === severityTypeId
    );

    const count = severityAssignment?.channels?.length || 0;

    if (count === 0) {
      return <span className={sharedClasses.selectText}>Select</span>;
    }
    return (
      <div className={sharedClasses.selectText}>
        Channels <BadgeCount number={count} />
      </div>
    );
  };

  const alertTypeAssignments = getUpdatedAlertTypes();

  return (
    <div>
      <EuiPanel
        hasShadow={false}
        hasBorder={true}
        style={{ backgroundColor: "var(--card-background)" }}>
        <EuiFlexGroup>
          <EuiFlexItem grow={3}>
            <h3 className={classes.subheading}>Alert Type Severity</h3>
          </EuiFlexItem>
          <EuiFlexItem grow={3}>
            <h3 className={classes.subheading} style={{ marginLeft: "-36px" }}>
              Notify on Open Events
            </h3>
          </EuiFlexItem>
          <EuiFlexItem grow={3}>
            <h3 className={classes.subheading} style={{ marginLeft: "-16px" }}>
              Notify on Closed Events
            </h3>
          </EuiFlexItem>
        </EuiFlexGroup>
        {rows.map((severity) => {
          const items = alertTypeAssignments.filter((a) => a.severityTypeName === severity);

          return (
            <div key={severity} className={classes.rowContainer}>
              <div className={classes.controlContainer}>
                <EuiFlexGroup className={sharedClasses.fullWidth} alignItems="center">
                  <EuiFlexItem grow={1}>
                    <h4
                      className={classes.severityHeading}
                      data-severity={severity as SeverityType}>
                      {severity}
                    </h4>
                  </EuiFlexItem>

                  <EuiFlexItem grow={1}>
                    <LinkToList
                      Copy={false}
                      Columns={alertTypeColumns}
                      Items={items}
                      PopOverMaxWidth={600}
                      ButtonTitle={
                        <EuiFlexGroup alignItems="center" gutterSize="s">
                          <EuiFlexItem>
                            <AlertIcon fill={items.length > 0 ? "#FF7E62" : "var(--text)"} />
                          </EuiFlexItem>
                          <EuiFlexItem
                            style={{
                              color: items.length > 0 ? "#FF7E62" : "var(--text)",
                            }}>
                            {items.length}
                          </EuiFlexItem>
                        </EuiFlexGroup>
                      }
                      betweenButton={{ name: "View Alerts Types", onClick: () => {} }}
                    />
                  </EuiFlexItem>

                  {["open", "closed"].map((notificationType, i) => (
                    <EuiFlexItem grow={3} key={`${severity}-${notificationType}`}>
                      <Popover
                        ownFocus={true}
                        isOpen={
                          isPopoverOpen[`${severity}-${notificationType}`] ===
                          `${severity}-${notificationType}`
                        }
                        onClose={() => handlePopoverToggle(severity, notificationType)}
                        anchorPosition="downLeft"
                        panelPaddingSize="none"
                        panelStyle={{ width: "400px" }}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            e.stopPropagation();
                            handlePopoverToggle(severity, notificationType);
                          }
                        }}
                        button={
                          <EuiButton
                            className={sharedClasses.selectButton}
                            fullWidth
                            style={{
                              background: "transparent",
                              border: "1px solid var(--border-color)",
                            }}
                            iconType="arrowDown"
                            iconSide="right"
                            onClick={() => handlePopoverToggle(severity, notificationType)}>
                            {getSelectText(severity, notificationType)}
                          </EuiButton>
                        }
                        children={
                          <>
                            <div>
                              <EuiSelectable
                                singleSelection={false}
                                isLoading={isLoading}
                                listProps={{ showIcons: false }}
                                searchable
                                searchProps={{
                                  placeholder: "Search notification channels...",
                                  compressed: false,
                                  css: {
                                    borderTopLeftRadius: 10,
                                    borderTopRightRadius: 10,
                                    borderBottom: ".6px solid var(--border-color)",
                                    boxShadow: "none",
                                    backgroundColor: "var(--card-background)",
                                  },
                                }}
                                options={severityOptions[severity]?.[notificationType] || []}
                                onChange={(newOptions) =>
                                  handleOptionsChange(newOptions, severity, notificationType)
                                }
                                renderOption={(option) => {
                                  const labelParts = option.label.split(":");
                                  const typePart = labelParts[0]?.trim();
                                  const namePart = labelParts.slice(1).join(":").trim();
                                  return (
                                    <div style={{ position: "relative", cursor: "pointer" }}>
                                      <EuiCheckbox
                                        id={`checkbox_${option.label}`}
                                        checked={option?.checked === "on"}
                                        onChange={() => {}}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                        label={
                                          <div
                                            className={classes.checkboxLabel}
                                            style={{
                                              position: "absolute",
                                              left: 0,
                                              paddingLeft: "24px",
                                            }}>
                                            <div>{typePart}</div>
                                            <div>{namePart}</div>
                                          </div>
                                        }
                                      />
                                    </div>
                                  );
                                }}>
                                {(list, search) => (
                                  <div style={{ width: "100%" }}>
                                    {search}
                                    <div className={classes.columnHeader}>
                                      <span style={{ fontWeight: 600 }}>Type</span>
                                      <span style={{ fontWeight: 600 }}>Name</span>
                                    </div>
                                    {list}
                                  </div>
                                )}
                              </EuiSelectable>
                            </div>
                            <EuiSpacer size="s" />
                            <EuiFlexGroup
                              gutterSize="m"
                              justifyContent="flexEnd"
                              style={{ padding: "8px", backgroundColor: "var(--app-background)" }}>
                              <EuiFlexItem grow={false}>
                                <Button
                                  text="Cancel"
                                  type="secondary"
                                  onClick={() => handlePopoverToggle(severity, notificationType)}
                                />
                              </EuiFlexItem>
                              <EuiFlexItem grow={false}>
                                <EuiButton
                                  fill
                                  minWidth={false}
                                  onClick={() => handlePopoverToggle(severity, notificationType)}
                                  size="s">
                                  Apply
                                </EuiButton>
                              </EuiFlexItem>
                            </EuiFlexGroup>
                          </>
                        }
                      />
                    </EuiFlexItem>
                  ))}
                </EuiFlexGroup>
              </div>
            </div>
          );
        })}
      </EuiPanel>
    </div>
  );
};

export default ProfileNotifications;
